import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_URL = process.env.REACT_APP_SITE_URL || 'https://myeaitradingcompass.com';

export default function SchemaMarkup() {
  // Organization Schema
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "EducationalOrganization",
    "name": "Trading Compass",
    "url": SITE_URL,
    "description": "A Trading Compass Between Your Analysis and Your Execution. Built for skilled traders who struggle with discipline, risk control, and emotional consistency.",
    "logo": `${SITE_URL}/logo-white.png`,
    "sameAs": [
      SITE_URL
    ]
  };

  // WebPage Schema
  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Trading Compass - Fix Your Execution Discipline",
    "description": "Built for traders who know how to analyse but struggle with discipline & execution. Start the 21-Day Trading Habit Challenge.",
    "url": SITE_URL
  };

  // Course Schema (21-Day Challenge)
  const courseSchema = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": "21-Day Trading Habit Challenge",
    "description": "A capital-readiness filter designed to expose execution & behavioural flaws, rebuild discipline through enforced structure, and create a repeatable process you can scale with capital.",
    "provider": {
      "@type": "Organization",
      "name": "Trading Compass",
      "url": SITE_URL
    },
    "educationalLevel": "Intermediate to Advanced",
    "timeRequired": "P21D"
  };

  // FAQ Schema
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Is Trading Compass a signal service?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "No. Trading Compass does not give buy/sell signals. It sits between your analysis and execution, enforcing risk, structure, and discipline before you place a trade."
        }
      },
      {
        "@type": "Question",
        "name": "I already know how to trade. How does this help me?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Trading Compass is built exactly for traders who know how to analyse but are still inconsistent. It focuses on: execution discipline, risk & R:R enforcement, and emotional decision control."
        }
      },
      {
        "@type": "Question",
        "name": "How is this different from a trading journal?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "A journal shows you mistakes after the damage is done. Trading Compass intervenes before the trade, forcing: stop loss & take profit clarity, risk validation, and execution accountability."
        }
      },
      {
        "@type": "Question",
        "name": "What is the 21-Day Trading Habit Challenge?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "It's a capital-readiness filter, not a course. The challenge is designed to: expose execution & behavioural flaws, rebuild discipline through enforced structure, and create a repeatable process you can scale with capital."
        }
      }
    ]
  };

  // Review Schema (Testimonials)
  const reviewSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "Trading Compass",
    "description": "Trading execution discipline platform",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "127"
    },
    "review": [
      {
        "@type": "Review",
        "author": {
          "@type": "Person",
          "name": "John D."
        },
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": "5"
        },
        "reviewBody": "I finally saw why I kept breaking rules."
      },
      {
        "@type": "Review",
        "author": {
          "@type": "Person",
          "name": "Sarah M."
        },
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": "5"
        },
        "reviewBody": "My trading became boring — and consistent."
      },
      {
        "@type": "Review",
        "author": {
          "@type": "Person",
          "name": "Mike K."
        },
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": "5"
        },
        "reviewBody": "For the first time, I trusted my execution."
      }
    ]
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(webPageSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(courseSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(faqSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(reviewSchema)}
      </script>
    </Helmet>
  );
}